import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';
import Card from 'components/ui/card';
import LoadingSpinner from 'shared/components/ui/loadingSpinner';
import View from 'components/ui/view';
import Text from 'components/ui/text';
import { LineItem } from 'components/cart/index';
import PaymentLogo from 'components/payment/logo';
import {
  isCanceledOrder,
  isFailedOrder,
  isTotalLineItem
} from 'shared/models/order';
import { engravingForItem } from 'shared/models/cart';
import { isEngraving } from 'shared/models/product';
import { loadOrder } from './actions';
import LocationIcon from 'images/icons/location-outline-small.svg?react-svg';
import PromoIcon from 'images/icons/promo-outline-black.svg?react-svg';

let OrderHistoryOrder = class OrderHistoryOrder extends React.Component {
  componentDidMount() {
    const oldOrder = this.props.order || {};
    const routeParams = this.props.match.params || {};
    if (oldOrder._id !== routeParams.id && !this.props.loading)
      this.props.loadOrder(routeParams.id);
  }

  render() {
    const { loading, order } = this.props;

    if (loading) {
      return (
        <View padding="md" className="order-history banner-padding">
          <Card block>
            <div
              style={{
                minHeight: '300px',
                position: 'relative',
                marginTop: '125px'
              }}
            >
              <LoadingSpinner black />
            </div>
          </Card>
        </View>
      );
    }

    if (!order) return null;

    const wasCanceled = isCanceledOrder(order) || isFailedOrder(order);

    const giftRecipient = order.gift_info.recipient;
    const giftMessage = order.gift_info.message;
    const { address, unit, zip } = order.delivery_address_detail;
    const displayAddress = [address, unit, zip]
      .filter(str => !_.isEmpty(str))
      .join(', ');
    const deliveryNotes = order.delivery_notes;

    const products = order.products.filter(p => !isEngraving(p)) || [];
    const engravings = order.products.filter(isEngraving) || [];
    const card = (order.charge && order.charge.source) || {};
    const lineItems = order.line_items.filter(line => !isTotalLineItem(line));
    const totalLineItem = _.find(order.line_items, isTotalLineItem) || {};
    const total = totalLineItem.display;
    const preTipTotal = order.total || 0;
    const tip = order.order_tip || 0;

    return (
      <View padding="md" className="order-history banner-padding">
        <View margin="xxs">
          <Text title tag="h2" weight="med" size="md" margin="none">
            {moment(order.order_date).format('MMMM D, YYYY')}
          </Text>
        </View>
        <View margin="sm">
          {wasCanceled ? (
            <Text title error size="xs" weight="bold">
              Canceled
            </Text>
          ) : (
            <Text title secondary size="xs" weight="med">
              Delivered {moment(order.order_date).format('MMMM D, YYYY h:mm A')}
            </Text>
          )}
        </View>
        <View margin="xs">
          <Link to="/orders" className="link-text text-secondary">
            View All Orders
          </Link>
        </View>
        <Card block>
          <View className="order-history-row" padding="sm">
            <div className="order-history-row__icon-container">
              <LocationIcon />
            </div>
            <View
              className="order-history-row__address-container"
              inlineBlock
              padding={{ sides: 'sm' }}
            >
              {giftRecipient && (
                <div className="text-overflow">
                  <Text tag="span" weight="med">
                    {giftRecipient}
                  </Text>
                  {giftMessage && ' -'}
                  {giftMessage && (
                    <span className="quote-text">{giftMessage}</span>
                  )}
                </div>
              )}
              <div className="text-overflow">{displayAddress}</div>
              {deliveryNotes && (
                <div className="quote-text text-overflow">{deliveryNotes}</div>
              )}
            </View>
          </View>

          <View padding={{ sides: 'md' }}>
            <View
              padding={{ top: 'md', bottom: 'sm' }}
              className="order-history-row"
            >
              <ul className="plain-list">
                {products.map(item => {
                  const engraving = engravingForItem(engravings, item);
                  const key = item.cart_id
                    ? item.cart_id + item.quantity
                    : item.sku_id + item.quantity;

                  return (
                    <li className="cart-item" key={key}>
                      <div className="row">
                        <div className="col-xs-9">
                          <div className="order-history-row__item-container">
                            <img
                              src={item.image}
                              className="cart-item__thumbnail"
                            />
                            <div className="inline-block">
                              <Text weight="bold" className="text-overflow">
                                {item.title} x {item.quantity}
                              </Text>
                              <div className="text-overflow">
                                {item.subtitle}
                              </div>
                              <div>{item.volume}</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-xs-3 text-right">
                          {item.subtotal ? (
                            <Fragment>
                              <Text success size="xs" weight="med">
                                <div className="inline-block promo-icon">
                                  <PromoIcon />
                                </div>
                                ${item.subtotal.toFixed(2)}
                              </Text>
                              <Text className="strikethrough" size="xs">
                                ${(item.discount_price * item.quantity).toFixed(
                                  2
                                )}
                              </Text>
                            </Fragment>
                          ) : (
                            <Text size="xs" weight="med">
                              {item.discount_price &&
                                `$${(item.discount_price * item.quantity
                                ).toFixed(2)}`}
                            </Text>
                          )}
                        </div>
                      </div>

                      {!!engraving && (
                        <div className="cart-item__engraving">
                          <hr className="light" />
                          <Text className="right" weight="med" size="xs">
                            {engraving.discount_price &&
                              `(+$${engraving.discount_price.toFixed(2)})`}
                          </Text>
                          <div className="text-bold">Engraving</div>
                          {engraving.meta &&
                            engraving.meta.line_1 && (
                              <div>{engraving.meta.line_1}</div>
                            )}
                          {engraving.meta &&
                            engraving.meta.line_2 && (
                              <div>{engraving.meta.line_2}</div>
                            )}
                          {engraving.meta &&
                            engraving.meta.line_3 && (
                              <div>{engraving.meta.line_3}</div>
                            )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </View>

            {!wasCanceled && (
              <View
                padding={{ top: 'sm', bottom: 'sm' }}
                className="order-history-row"
              >
                <div className="row">
                  {lineItems.map(line => (
                    <div
                      key={line.title + '-' + line.display}
                      className="col-xs-12"
                    >
                      <LineItem {...line} />
                    </div>
                  ))}
                </div>
              </View>
            )}

            <View padding={{ top: 'md', bottom: 'md' }}>
              <div className="row middle-xs">
                <div className="col-xs-9">
                  {card.brand && (
                    <PaymentLogo brand={card.brand} className="left" />
                  )}
                  {card.brand && (
                    <span className="col-xs">{`${card.brand ||
                      ''} ${card.last4 || ''}`}</span>
                  )}
                </div>
                <div className="col-xs-3">
                  <Text size="lg" weight="bold" className="text-right">
                    {total}
                  </Text>
                </div>
              </div>

              {!!preTipTotal &&
                !!tip && (
                  <div className="row">
                    <div className="col-xs-12">
                      <Text secondary weight="light">
                        Your card was charged ${preTipTotal.toFixed(2)} + ${tip.toFixed(2)}{' '}
                        for tip.
                      </Text>
                    </div>
                  </div>
                )}
            </View>
          </View>
        </Card>

        {wasCanceled && (
          <View padding={{ top: 'xs', sides: 'md' }}>
            <Text tag="p" secondary className="text-center">
              If a cancellation occurred after the order was picked up or if a
              delivery was attempted and the customer was unavailable to receive
              the delivery, a cancellation fee may apply.
            </Text>
          </View>
        )}
      </View>
    );
  }
};

OrderHistoryOrder = connect(
  state => ({
    order: state.orderHistory.order,
    loading: state.orderHistory.orderLoading
  }),
  { loadOrder }
)(OrderHistoryOrder);

export default OrderHistoryOrder;



// WEBPACK FOOTER //
// ./shared/components/orderHistory/order.js